import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import UITour from 'ui-shepherd-react/tour/UITour';
import MultiTourHandler from 'ui-shepherd-react/lib/MultiTourHandler';
import OnboardingTourSteps from './OnboardingTourSteps';
import { OnboardingTourTrackingProvider } from './OnboardingTourTrackingProvider';
import getTour from '../util/getTour';
import { buildTourConfig } from '../util/tourConfig';
import { getTourIdFromQueryParams, getQueryParam } from '../util/queryParams';
import { getNextTourFromStorage } from '../util/tourStorage';
import { transformTour } from '../util/tourTransformer';
import { getStorageItem, setStorageItem } from '../util/storageUtil';
import { parseReturnUrl } from '../util/urlUtils';
import { Metrics } from '../util/metrics';
import SentryManager from '../manager/SentryManager';
const SELENIUM_DISABLE_ONBOARDING_TOURS_KEY = 'selenium.disable.onboarding_tours';

const shouldDisableOnboardingTour = () => {
  return (// TODO: add link to selenium doc
    // Use the convention selenium.disable.<feature>.
    // selenium.disable.onboarding_tours = 'true' should prevent the tour from loading.
    getStorageItem(SELENIUM_DISABLE_ONBOARDING_TOURS_KEY) === 'true'
  );
};

const SELENIUM_ONBOARDING_TOUR_FLAG = 'selenium_onboarding_tour_enabled';
const SELENIUM_ONBOARDING_TOUR_CONFIG = 'selenium_onboarding_tour_config';

const setOnboardingTourConfigForSelenium = tourData => {
  if (getStorageItem(SELENIUM_ONBOARDING_TOUR_FLAG) === 'true') {
    setStorageItem(SELENIUM_ONBOARDING_TOUR_CONFIG, JSON.stringify(tourData));
  }
};

const multiTourHandler = new MultiTourHandler();

function OnboardingTour(props) {
  const [activeTourId, setActiveTourId] = useState(null);
  const [tourState, setTourState] = useState({
    tourData: null,
    tourConfig: null
  });
  useEffect(() => {
    if (shouldDisableOnboardingTour()) {
      return;
    }

    let tourId = props.tourId || getTourIdFromQueryParams();
    let prevTourId;
    let prevTourLength = 0;
    let linkedToursTotalStepCountFromStorage;
    let returnUrl = parseReturnUrl(getQueryParam('returnUrl'));

    if (!tourId) {
      // Tours from storage are set through the nextTourAlias tour config property.
      const tourDataFromStorage = getNextTourFromStorage();
      tourId = tourDataFromStorage.tourId;
      prevTourId = tourDataFromStorage.prevTourId;
      prevTourLength = tourDataFromStorage.prevTourLength || 0;
      linkedToursTotalStepCountFromStorage = tourDataFromStorage.linkedToursTotalStepCount;
      returnUrl = tourDataFromStorage.returnUrl;
    }

    if (!tourId) {
      return;
    }

    setActiveTourId(tourId);
    getTour(tourId).then(tourData => {
      tourData.steps = transformTour(tourData.steps);
      tourData.prevTourLength = prevTourLength;
      tourData.linkedToursTotalStepCount = tourData.linkedToursTotalStepCount || linkedToursTotalStepCountFromStorage;
      tourData.returnUrl = returnUrl;
      const newTourConfig = buildTourConfig(tourId, tourData, prevTourId);
      setTourState({
        tourConfig: newTourConfig,
        tourData
      });
      setOnboardingTourConfigForSelenium(tourData);
    }).catch(error => {
      SentryManager.reportLoadTourError(error, tourId);
      Metrics.counter('load-onboarding-tour-error', {
        tourId: tourId
      }).increment();
    });
  }, [props.tourId]);
  const {
    tourConfig,
    tourData
  } = tourState;

  if (!tourData || !tourConfig || !activeTourId) {
    return null;
  }

  return /*#__PURE__*/_jsx(OnboardingTourTrackingProvider, {
    tourId: activeTourId,
    children: /*#__PURE__*/_jsx(UITour, {
      className: "onboarding-tour-container",
      config: tourConfig,
      multiTourHandler: multiTourHandler,
      tour: activeTourId,
      children: /*#__PURE__*/_jsx(OnboardingTourSteps, {
        tourData: tourData,
        tourId: activeTourId
      })
    })
  });
}

OnboardingTour.propTypes = {
  tourId: PropTypes.string
};
export default /*#__PURE__*/memo(OnboardingTour);
export const WEBPACK_3_FORCE_MODULE_IMPORT = 1;